import { Box } from '@byecode/ui'
import { DevicePreviewEnum } from '@lighthouse/core'
import { getFileSizeWithUnit, isMiniprogram } from '@lighthouse/tools'
import { Drawer } from '@mantine/core'
import cls from 'classnames'
import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FILE_ICON_COLOR_MAP, FILE_ICON_MAP } from '../../../constants/common'
import { useApplicationContext } from '../../../contexts'
import type { EnableActionsParams, FileItemAction, WithStatusFile } from '../../../types'
import { downloadSingleFileWithUrl, getAuthFile, getImageFullUrlInApplication } from '../../../utils'
import { resolveWechatShareFileName } from '../../FilePreviewer'
import { FILE_OPERATION_OPTIONS, FileItemActionLabelMap } from '../constants'
import FileItem from '../mobile/FileItem'
import type { FileOperationOption } from '../types'
import { getFileDrawerConfig } from '../WithoutPopperFileUploader'
import * as SC from './styles'
import { TooltipIcon } from './TooltipIcon'

const DEFAULT_ACTIONS: EnableActionsParams = { canDownload: true, canPreview: true, canDelete: true }

interface FileListItemProps {
    children?: React.ReactNode
    data: WithStatusFile
    enableActions?: EnableActionsParams
    variant?: 'card' | 'normal'
    previewType: DevicePreviewEnum
    disabled?: boolean
    // disableDownload?: boolean
    onRemove?: (fileId: WithStatusFile['uid']) => void
    onPreview?: () => void
}

const FileListItem: React.FC<FileListItemProps> = ({
    data,
    enableActions = DEFAULT_ACTIONS,
    variant = 'normal',
    previewType,
    disabled,
    onRemove,
    onPreview
}) => {
    const { uid, type, url, name, status, percent, size } = data
    const [opened, setOpened] = useState(false)
    const { pageTarget, appId, fetchAVPlayUrl, miniProgramId, sendMessageToMiniProgram } = useApplicationContext()
    const { t } = useTranslation()

    const disabledTooltip = previewType === DevicePreviewEnum.MOBILE

    const isHasActions = useMemo(() => {
        return !!enableActions?.canDownload || !!enableActions?.canPreview || !!enableActions?.canDelete
    }, [enableActions?.canDelete, enableActions?.canDownload, enableActions?.canPreview])

    // const menuData: EnableActionsParams = useMemo(() => {
    //     if (!enableActions) {
    //         return {}
    //     }
    //     return typeof enableActions === 'boolean' ? DEFAULT_ACTIONS : enableActions
    // }, [enableActions])

    const filterOptions: FileOperationOption[] = useMemo(() => {
        const { canPreview, canDelete, canDownload } = enableActions
        const actions: Record<FileItemAction, boolean | undefined> = {
            download: canDownload,
            preview: canPreview,
            remove: canDelete
        }
        return FILE_OPERATION_OPTIONS.filter(item => {
            if (item.value === 'download') {
                return !(!canDownload || !actions[item.value])
            }
            return actions[item.value]
        })
    }, [enableActions])

    const handlers = useMemo(() => {
        return {
            async download() {
                // 小程序环境特殊处理
                if (isMiniprogram() && miniProgramId) {
                    const file = {
                        name,
                        type,
                        url
                    }
                    const authFile = await getAuthFile(file, fetchAVPlayUrl)
                    await sendMessageToMiniProgram?.({
                        messageType: 'MINI_PROGRAM_DOWNLOAD_FILE',
                        wsId: miniProgramId,
                        fileUrl: authFile.url,
                        fileName: resolveWechatShareFileName(authFile)
                    })
                    return
                }

                if ((type === 'video' || type === 'audio') && fetchAVPlayUrl) {
                    const auth = await fetchAVPlayUrl(url, true)
                    if (auth) {
                        window.open(auth.playUrl)
                    }
                } else {
                    await downloadSingleFileWithUrl({ url, fileName: resolveWechatShareFileName({ name, url, type }) })
                }
            },
            preview() {
                onPreview?.()
            },
            rename() {
                //
            },
            remove() {
                onRemove?.(uid)
            }
        }
    }, [fetchAVPlayUrl, miniProgramId, name, onPreview, onRemove, sendMessageToMiniProgram, type, uid, url])

    const handleAction = useCallback(
        (action: FileItemAction) => {
            handlers[action]()
            setOpened(false)
        },
        [handlers]
    )

    const handleDeleteFileItem = useCallback(() => {
        onRemove?.(uid)
    }, [onRemove, uid])

    const handlePreview = useCallback(
        (status: string) => {
            if (status === 'success') {
                handleAction('preview')
            }
        },
        [handleAction]
    )

    const fileIcon = useMemo(() => {
        return type === 'image' ? (
            url && (
                <SC.ImageThumb
                    title={name}
                    status={status}
                    src={getImageFullUrlInApplication(appId, url)}
                    onClick={() => handlePreview(status)}
                />
            )
        ) : FILE_ICON_MAP[type] ? (
            <SC.FileIcon
                type={FILE_ICON_MAP[type]}
                color={FILE_ICON_COLOR_MAP[type]}
                status={status}
                size={24}
                onClick={() => handlePreview(status)}
            />
        ) : (
            <SC.FileIcon
                type={FILE_ICON_MAP.other}
                color={FILE_ICON_COLOR_MAP.other}
                status={status}
                size={24}
                onClick={() => handlePreview(status)}
            />
        )
    }, [appId, handlePreview, name, status, type, url])

    const fileActionIcon = useMemo(() => {
        switch (status) {
            case 'error': {
                return (
                    <SC.MoreErrorContainer>
                        <SC.ErrorIcon type="WarningCircle" />
                        <SC.CloseIcon type="Close" onClick={handleDeleteFileItem} />
                    </SC.MoreErrorContainer>
                )
            }
            case 'uploading': {
                return (
                    <SC.MoreUploadingContainer>
                        <SC.MoreUploadingPercent>{Math.ceil(percent ?? 0)}%</SC.MoreUploadingPercent>
                        {/* <RingProgress size={20} thickness={4} sections={[{ value: percent ?? 0, color: 'var(--color-gray-500)' }]} /> */}
                    </SC.MoreUploadingContainer>
                )
            }
            case 'success': {
                if (typeof enableActions === 'boolean') {
                    return
                }
                if (previewType === DevicePreviewEnum.MOBILE) {
                    return (
                        <SC.MoreSuccessContainer
                            data-field-other-icon
                            data-field-active={false}
                            data-field-disabled={!isHasActions}
                            defaultOpacity={1}
                            style={{ justifyContent: 'flex-end' }}
                        >
                            <TooltipIcon
                                key={name}
                                disabled={disabledTooltip}
                                label={t('more')}
                                icon="DotsThree"
                                onClick={() => setOpened(true)}
                            />
                        </SC.MoreSuccessContainer>
                    )
                }
                return (
                    <SC.MoreSuccessContainer>
                        {filterOptions.map(({ value }) => {
                            const item = FileItemActionLabelMap[value]
                            return (
                                <Box key={value} data-field-other-icon data-field-disabled={!isHasActions}>
                                    <TooltipIcon
                                        {...item}
                                        disabled={disabledTooltip}
                                        label={t(item.label)}
                                        onClick={() => handleAction(value)}
                                    />
                                </Box>
                            )
                        })}
                    </SC.MoreSuccessContainer>
                )
            }
            default: {
                return null
            }
        }
    }, [
        disabledTooltip,
        enableActions,
        filterOptions,
        handleAction,
        handleDeleteFileItem,
        isHasActions,
        name,
        percent,
        previewType,
        status,
        t
    ])

    const fileNameText = useMemo(() => {
        const sizeText = size ? getFileSizeWithUnit(size) : ''
        return sizeText ? `${name} (${sizeText})` : name
    }, [name, size])

    return (
        <SC.FileListItemWrapper variant={variant} data-field-other data-field-active={false} data-field-disabled={disabled}>
            <SC.FileInfo className={cls({ 'not-uploaded': status !== 'success' })} onClick={() => onPreview?.()}>
                {fileIcon}
                <SC.FileName key="info">{fileNameText}</SC.FileName>
            </SC.FileInfo>
            {fileActionIcon}
            {/* 移动端图片操作 */}
            <Drawer {...getFileDrawerConfig()} target={pageTarget} opened={opened} onClose={() => setOpened(false)}>
                <FileItem handleAction={handleAction} options={filterOptions} onClose={() => setOpened(false)} />
            </Drawer>
        </SC.FileListItemWrapper>
    )
}

export default FileListItem
